import { request } from '@/api/client'
import type { Localized } from '@/i18n'

export interface ExtRecord {
  id: number
  entity: string
  ref: string
  status: string | null
  title: string | null
  data: Record<string, unknown>
  createdAt: number
  createdBy: string | null
  updatedAt: number
  updatedBy: string | null
}

export interface ExtHistory {
  at: number
  username: string | null
  action: string
  fromStatus: string | null
  toStatus: string | null
  note: string | null
  changes: string | null
}

export interface ExtNotification {
  id: number
  at: number
  category: 'operations' | 'finance' | 'customerService' | 'integrations' | 'system'
  severity: 'info' | 'success' | 'warning' | 'danger'
  title: Localized
  body: string | null
  link: string | null
  read: boolean
  actor: string | null
}

export interface SlaReport {
  total: number
  delivered: number
  returned: number
  open: number
  successRate: number
  avgDeliveryHours: number | null
  truncated: boolean
  buckets: { within24h: number; within48h: number; within72h: number; over72h: number }
  byCity: { name: string; total: number; delivered: number; rate: number }[]
  byClient: { name: string; total: number; delivered: number; rate: number }[]
  byRunner: { name: string; total: number; delivered: number; rate: number }[]
}

export interface WarehouseSummary {
  onHand: number
  codOnHand: number
  truncated: boolean
  aging: { lt24h: number; d1to3: number; d3to7: number; gt7d: number }
  byStatus: { name: string; count: number }[]
  byCell: { name: string; count: number }[]
  byCity: { name: string; count: number }[]
  byBranch: { name: string; count: number }[]
  oldest: { awb: string; statusName: string; branchName: string; cellName: string; toCityName: string; auditDate: string; ageHours: number }[]
}

export const extService = {
  async list(entity: string, q: { page: number; pageSize: number; status?: string; q?: string; filters?: Record<string, string> }, signal?: AbortSignal) {
    const query: Record<string, string | number | undefined> = { page: q.page, pageSize: q.pageSize, status: q.status || undefined, q: q.q || undefined }
    for (const [k, v] of Object.entries(q.filters ?? {})) if (v) query[`f.${k}`] = v
    const res = await request<ExtRecord[]>(`/ext/records/${entity}`, { query, signal })
    return { rows: res.data, total: res.meta?.total ?? res.data.length, counts: (res.meta as { counts?: Record<string, number> } | undefined)?.counts ?? {} }
  },
  get: async (entity: string, id: number, signal?: AbortSignal) => (await request<{ record: ExtRecord; history: ExtHistory[] }>(`/ext/records/${entity}/${id}`, { signal })).data,
  create: async (entity: string, data: Record<string, unknown>) => (await request<ExtRecord>(`/ext/records/${entity}`, { method: 'POST', body: data })).data,
  update: async (entity: string, id: number, data: Record<string, unknown>) => (await request<ExtRecord>(`/ext/records/${entity}/${id}`, { method: 'PUT', body: data })).data,
  remove: (entity: string, id: number) => request(`/ext/records/${entity}/${id}`, { method: 'DELETE', body: {} }),
  action: async (entity: string, id: number, action: string, note?: string) => (await request<ExtRecord>(`/ext/records/${entity}/${id}/actions/${action}`, { method: 'POST', body: { note } })).data,
  approvals: async (signal?: AbortSignal) => (await request<ExtRecord[]>('/ext/approvals', { signal })).data,
  async notifications(q: { category?: string; unread?: boolean; limit?: number }, signal?: AbortSignal) {
    const res = await request<ExtNotification[]>('/ext/notifications', { query: { category: q.category, unread: q.unread ? 'true' : undefined, limit: q.limit ?? 100 }, signal })
    return { items: res.data, unread: (res.meta as { unread?: number } | undefined)?.unread ?? 0 }
  },
  markRead: (ids: number[] | 'all') => request('/ext/notifications/read', { method: 'POST', body: { ids } }),
  preferences: async () => (await request<{ categories: Record<string, { inApp: boolean; sound: boolean; email: boolean }> }>('/ext/preferences')).data,
  savePreferences: (data: { categories: Record<string, { inApp: boolean; sound: boolean; email: boolean }> }) => request('/ext/preferences', { method: 'PUT', body: data }),
  async uploadFile(file: File) {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })
    return (await request<{ id: string; name: string; mime: string; size: number }>('/ext/files', { method: 'POST', body: { name: file.name, mime: file.type, base64 } })).data
  },
  fileUrl: (id: string) => `/api/v1/ext/files/${encodeURIComponent(id)}`,
  async integrationLogs(q: { page: number; pageSize: number; failed?: boolean }, signal?: AbortSignal) {
    const res = await request<{ id: number; at: number; webhookId: number; event: string; url: string; attempt: number; success: number; responseCode: number | null; error: string | null; durationMs: number }[]>('/ext/integration-logs', { query: { page: q.page, pageSize: q.pageSize, failed: q.failed ? 'true' : undefined }, signal })
    return { rows: res.data, total: res.meta?.total ?? res.data.length }
  },
  testWebhook: (id: number) => request(`/ext/webhooks/${id}/test`, { method: 'POST', body: {} }),
  sla: async (q: { from: string; to: string; branchId?: number; clientId?: number }, signal?: AbortSignal) => (await request<SlaReport>('/ext/analytics/sla', { query: q, signal })).data,
  warehouse: async (q: { branchId?: number }, signal?: AbortSignal) => (await request<WarehouseSummary>('/ext/analytics/warehouse', { query: q, signal })).data,
}
